import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * FormSection — primitive archetype "formulaire-metier"
 *
 * Segmente les formulaires longs en blocs titre + description + champs.
 * Utilise par les pages archetype-form : AuditProposalBuilder, InvoiceBuilder,
 * fiches client, parametres compte, etc.
 *
 * Slots :
 *   - title       : string ou ReactNode — titre de la section
 *   - description : string ou ReactNode — aide contextuelle optionnelle
 *   - action      : ReactNode optionnel — bouton/lien en haut a droite
 *                   (ex: "Ajouter un champ", "Dupliquer la section")
 *   - children    : ReactNode — les champs du formulaire
 *                   (Label + Input, en general wrappe dans un grid ou space-y)
 *
 * Variantes :
 *   - layout : 'stacked' (defaut, title au-dessus) | 'split' (title a gauche, form a droite)
 *   - tone   : 'plain' (defaut) | 'card' (bordure + bg surface-card + padding)
 *
 * Exemple stacked plain :
 *   <FormSection
 *     title="Informations client"
 *     description="Coordonnees utilisees pour la facturation."
 *   >
 *     <div className="grid grid-cols-2 gap-4">
 *       <div><Label>Nom</Label><Input /></div>
 *       <div><Label>SIRET</Label><Input /></div>
 *     </div>
 *   </FormSection>
 *
 * Exemple split card (layout settings Stripe-like) :
 *   <FormSection
 *     layout="split"
 *     tone="card"
 *     title="Notifications"
 *     description="Choisis quand recevoir un email."
 *     action={<Button variant="ghost" size="sm">Reinitialiser</Button>}
 *   >
 *     <Switch /> <Switch />
 *   </FormSection>
 */
const formSectionVariants = cva('', {
  variants: {
    layout: {
      stacked: 'space-y-4',
      split:   'grid grid-cols-1 md:grid-cols-3 gap-6',
    },
    tone: {
      plain: '',
      card:  'rounded-xl border border-ink-ghost/30 bg-surface-card p-6 shadow-sm',
    },
  },
  defaultVariants: {
    layout: 'stacked',
    tone:   'plain',
  },
})

export interface FormSectionProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof formSectionVariants> {
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
}

const FormSection = React.forwardRef<HTMLElement, FormSectionProps>(
  ({ className, layout, tone, title, description, action, children, ...props }, ref) => {
    const isSplit = layout === 'split'

    const header = (
      <div className={cn('flex items-start justify-between gap-4', isSplit ? 'md:col-span-1' : '')}>
        <div className="min-w-0 space-y-1">
          {typeof title === 'string' ? (
            <h3 className="text-h3 text-ink font-semibold">{title}</h3>
          ) : (
            title
          )}
          {description ? (
            typeof description === 'string' ? (
              <p className="text-body text-ink-muted">{description}</p>
            ) : (
              description
            )
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    )

    const body = (
      <div className={cn(isSplit ? 'md:col-span-2' : '')}>{children}</div>
    )

    return (
      <section
        ref={ref}
        className={cn(formSectionVariants({ layout, tone }), className)}
        {...props}
      >
        {header}
        {body}
      </section>
    )
  }
)
FormSection.displayName = 'FormSection'

export { FormSection, formSectionVariants }
